"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Loader, CircleCheck, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
export default function ConsultWithARecoverySpecialist() {
  return <section id="consult-with-a-recovery-specialist" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4">
        <div className="mx-auto max-w-[800px]">
          {}
          <div className="mb-12 text-center">
            
            <h2 className="text-foreground mb-4 text-3xl font-bold tracking-tight md:text-4xl">
              Consult with a Recovery Specialist
            </h2>
            <blockquote className="text-muted-foreground mx-auto max-w-2xl text-lg leading-relaxed">
              "At Catalyst Resource Group, we believe catalyst recovery should be
              simple, transparent, and profitable for your facility. I'm here to
              help you navigate the process and ensure you get the maximum value
              from your spent materials."
            </blockquote>
            <div className="mt-4">
              <p className="text-foreground font-semibold">Curt Harke</p>
              <p className="text-muted-foreground text-sm">
                Director of Recovery Operations
              </p>
            </div>
          </div>

          {}
          <div className="bg-card rounded-xl border border-border p-6 shadow-sm md:p-10">
            <FormProvider formId="consultation_form_talk_to_an_expert" sectionName="consult-with-a-recovery-specialist" defaultValues={{
            name: "",
            email: "",
            phone: "",
            company: "",
            role: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            company: {
              required: true
            },
            role: {
              required: false
            },
            message: {
              required: false
            }
          }} submitText="Request Consultation" successMessage="Thank you for your interest. We have received your request and will contact you shortly to discuss your recovery needs.">
              <ConsultationForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-300">
        <div className="bg-accent mb-6 flex size-16 items-center justify-center rounded-full">
          <CircleCheck className="text-ring size-8" />
        </div>
        <h3 className="text-foreground mb-2 text-2xl font-bold">
          Request Received
        </h3>
        <p className="text-muted-foreground mb-8 max-w-md text-lg">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" size="lg">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid gap-6 md:grid-cols-2">
        {}
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-base">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="h-12 text-base" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-base">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="h-12 text-base" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {}
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-base">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" className="h-12 text-base" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="company" rules={{
        required: "Company name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-base">Company / Facility</FormLabel>
              <FormControl>
                <Input placeholder="Acme Refinery" className="h-12 text-base" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="role" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-base">Your Role</FormLabel>
            <FormControl>
              <div className="relative">
                <select className={cn("border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-12 w-full appearance-none rounded-md border px-3 py-2 text-base focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50", !field.value && "text-muted-foreground")} {...field}>
                  <option value="" disabled>
                    Select your role
                  </option>
                  {["Plant Manager", "Procurement", "Sustainability Manager", "Operations Engineer", "Other"].map((option, i) => <option key={i} value={option} className="text-foreground">
                      {option}
                    </option>)}
                </select>
                <ChevronDown className="text-muted-foreground pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 opacity-50" />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-base">
              What would you like to discuss?
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Briefly describe your needs or questions..." className="min-h-[120px] resize-y text-base" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      {submitError && <div className="bg-destructive/10 text-destructive rounded-md p-4 text-sm font-medium">
          {submitError}
        </div>}

      {}
      <Button type="submit" disabled={isSubmitting} className="bg-ring text-background hover:bg-ring/90 w-full text-lg font-semibold md:h-12" size="lg">
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Sending Request...
          </> : "Request Consultation"}
      </Button>

      <p className="text-muted-foreground text-center text-sm">
        Prefer to talk now? Call us directly at{" "}
        <Link to="tel:9512199312" className="text-ring hover:text-ring/80 font-medium underline underline-offset-4">
          (951) 219-9312
        </Link>
      </p>
    </Form>;
}